import React, { useState } from 'react';
import AdminOnly from '../components/AdminOnly';
import useProfile from '../hooks/useProfile';
import useAiStatus from '../hooks/reactQuery/useAiStatus';
import {
  useAiSchedules,
  useCreateAiScheduleFromPrompt,
} from '../hooks/reactQuery/useAiSchedules';
import ScheduleCard from '../components/schedules/ScheduleCard';

const NOT_CONFIGURED = 'The assistant is not configured on this environment.';
const HEAD_TEXT =
  'Each schedule runs its prompt on its own and emails you the answer. Pause, edit the prompt, or delete them here; create them here or from Ask AI.';

export const NEW_SCHEDULE_PLACEHOLDER =
  'Every Monday at 8 am, email me the orders that have been waiting in Ready for Kitting for more than 2 days, and any kit lots that expire in the next 90 days.';

// The person's own schedules: recurring prompts the assistant runs and emails to them. Creating
// one is a sentence in plain language; the assistant turns it into the schedule.
const Schedules = () => {
  const { email } = useProfile();
  const status = useAiStatus();
  const list = useAiSchedules();
  const create = useCreateAiScheduleFromPrompt();
  const [composing, setComposing] = useState(false);
  const [prompt, setPrompt] = useState('');

  const unavailable =
    status.data && !status.data.available
      ? status.data.reason || NOT_CONFIGURED
      : null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = prompt.trim();
    if (!text || create.isLoading) return;
    create.mutate(
      { prompt: text },
      {
        onSuccess: (result) => {
          if (result.schedules.length) {
            setPrompt('');
            setComposing(false);
          }
        },
      },
    );
  };

  const reply = create.data;

  return (
    <AdminOnly page="Schedules">
      <div className="page-disclaimer page-disclaimer--email" role="note">
        <b>Emails go only to you.</b>
        <span>
          Anything a schedule sends is delivered to the email on your account
          {email ? (
            <>
              {' '}
              (<span className="mono">{email}</span>)
            </>
          ) : null}
          , never to anyone else. To share it, forward the email on.
        </span>
      </div>
      {unavailable ? (
        <div className="page-note chat-unavailable" role="status">
          <b>The assistant isn&apos;t available here.</b> {unavailable}
        </div>
      ) : null}
      <div className="schedules">
        <div className="schedules-head">
          <div>
            <b>Schedules</b>
          </div>
          <span className="chat-sub">{HEAD_TEXT}</span>
          <button
            type="button"
            className="btn-app btn-app--primary"
            onClick={() => setComposing((c) => !c)}
            disabled={Boolean(unavailable)}
            aria-expanded={composing}
          >
            New schedule
          </button>
        </div>

        {composing ? (
          <form className="schedule-compose" onSubmit={submit}>
            <label htmlFor="schedule-prompt">
              Describe what you want and when. The assistant works out the rest.
            </label>
            <textarea
              id="schedule-prompt"
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={NEW_SCHEDULE_PLACEHOLDER}
              disabled={create.isLoading}
            />
            <div className="schedule-compose-actions">
              <button
                type="submit"
                className="btn-app btn-app--primary"
                disabled={!prompt.trim() || create.isLoading}
              >
                {create.isLoading ? 'Creating…' : 'Create'}
              </button>
              <button
                type="button"
                className="btn-app btn-app--outline"
                onClick={() => {
                  setComposing(false);
                  create.reset();
                }}
                disabled={create.isLoading}
              >
                Cancel
              </button>
            </div>
            {create.error ? (
              <div className="chat-error" role="alert">
                <b>The schedule was not created</b>
                <div className="chat-error-guidance">
                  {create.error.message}
                </div>
              </div>
            ) : null}
            {reply && reply.schedules.length === 0 ? (
              <div className="schedule-reply" role="status">
                <b>The assistant needs more detail.</b>
                <div>{reply.error ?? reply.reply}</div>
              </div>
            ) : null}
          </form>
        ) : null}

        {reply && reply.schedules.length > 0 && !composing ? (
          <div className="schedule-reply schedule-reply--ok" role="status">
            {reply.reply}
          </div>
        ) : null}

        {list.isLoading ? (
          <div className="schedules-empty">Loading…</div>
        ) : null}
        {list.error ? (
          <div className="schedules-empty">
            Could not load your schedules. {list.error.message}
          </div>
        ) : null}
        {list.data && list.data.length === 0 ? (
          <div className="schedules-empty">
            No schedules yet. Create one here, or ask the assistant for
            something recurring.
          </div>
        ) : null}
        {(list.data ?? []).map((schedule) => (
          <ScheduleCard key={schedule.id} schedule={schedule} />
        ))}
      </div>
    </AdminOnly>
  );
};

export default Schedules;
